import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import {
  PackageSearchIcon,
  FileSpreadsheetIcon,
  ThermometerSnowflake,
  PackageXIcon,
  TruckIcon,
  WalletIcon,
  BoxesIcon,
} from 'lucide-react';
import {
  TRACKING_V2_ROUTES,
  SCHEDULED_REPORTS_ROUTES,
  TEMPERATURE_CONTROL_ROUTES,
  VIAGENS_CONTROLE_ROUTES,
  RESTRICTED_PRODUCTS_ROUTES,
  INVENTORY_2_ROUTES,
} from 'lib/routes';
import { useAuth } from 'hooks';
import { RootState } from 'store';
import { setRecall } from 'store/ducks/leftPanel/recall';
import { usePermission } from 'hooks/usePermission';
import { ConfirmationDialog, ConfirmationDialogRef } from 'components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import * as S from './styles';

interface MenuItem {
  icon: React.ReactNode;
  title: string;
  info?: string;
  to: string;
  href?: string;
  isAllowed: boolean;
  requiresConfirmation?: boolean;
}

const LeftPanel: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { userBelongsToAnyOf, profile } = useAuth();
  const { hasPermissionTo } = usePermission();
  const { data: recall } = useSelector((state: RootState) => state.recallSlice);
  const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);
  const isCarrierMember = hasPermissionTo([
    'CARRIER-MEMBERS.FINDMEMBERS',
    'CARRIER-MEMBERS.FINDMEMBER',
  ]);

  const handleCollapse = useCallback(
    () => dispatch(setRecall(!recall)),
    [dispatch, recall]
  );

  const menuItems = useMemo((): MenuItem[] => {
    const isPlannerUser = profile?.type === 'planner';

    return [
      {
        icon: <S.BarChartIcon />,
        title: 'Dashboard',
        info: 'Gerencie seus indicadores.',
        to: '/dashboard',
        isAllowed: isCarrierMember && !isPlannerUser,
      },
      // {
      //   icon: <S.InvoiceIcon />,
      //   title: 'Notas Fiscais [OLD]',
      //   info: 'Curadoria das notas fiscais de importação e exportação.',
      //   to: '/',
      //   isAllowed: !isPlannerUser,
      // },
      {
        icon: <S.InvoiceIcon />,
        title: 'Notas Fiscais',
        info: 'Curadoria das notas fiscais de importação e exportação.',
        to: TRACKING_V2_ROUTES.invoicesTracking().url,
        isAllowed: !isPlannerUser,
      },
      {
        icon: <ThermometerSnowflake />,
        title: 'Controle de Temperatura',
        info: 'Monitoramento e controle da temperatura.',
        to: TEMPERATURE_CONTROL_ROUTES.list().url,
        isAllowed:
          !isPlannerUser && hasPermissionTo(['TEMPERATURE_CONTROL.FETCH']),
      },
      {
        icon: <TruckIcon />,
        title: 'Estadias',
        info: 'Controle de estadias',
        to: VIAGENS_CONTROLE_ROUTES.monitoring().url,
        isAllowed:
          !isPlannerUser && hasPermissionTo(['ACCESS_VIAGENS_CONTROLE']),
      },
      {
        icon: <FileSpreadsheetIcon />,
        title: 'Relatórios',
        info: 'Relatórios',
        to: SCHEDULED_REPORTS_ROUTES.invoicesTracking().url,
        isAllowed: !isPlannerUser,
      },
      {
        icon: <PackageSearchIcon />,
        title: 'Inspeção Estoque',
        info: 'Gerenciamento do processo de coleta de interditados.',
        to: '/inspecao-estoque/produtos-bloqueados',
        isAllowed:
          !!profile &&
          ['admin', 'warehouseMember', 'planner'].includes(profile.type),
      },
      {
        icon: <PackageXIcon />,
        title: 'Produtos Restritos',
        info: 'Gerenciamento de produtos restritos.',
        to: RESTRICTED_PRODUCTS_ROUTES.list().url,
        isAllowed: hasPermissionTo(
          RESTRICTED_PRODUCTS_ROUTES.list().permissions
        ),
      },
      {
        icon: <BoxesIcon />,
        title: 'Inventory 2',
        info: 'Upload das planilhas de inventory.',
        to: INVENTORY_2_ROUTES.importFiles().url,
        isAllowed:
          !!profile &&
          ['admin', 'warehouseMember', 'planner'].includes(profile.type),
      },
      {
        icon: <S.ListIcon />,
        title: 'Painel Gerencial',
        info: 'Acompanhe/busque os agendamento em formato de lista.',
        to: '/agendamento',
        isAllowed: !isPlannerUser,
      },
      // {
      //   icon: <S.CalendarIcon />,
      //   title: 'Agendamento',
      //   info: 'Acompanhe os agendamento em formato de calendário e por doca.',
      //   to: '/agendamento-calendario',
      //   isAllowed: true,
      // },
      {
        icon: <S.UserPinIcon />,
        title: 'Meus Dados',
        info: 'Altere seus dados de acesso ao sistema.',
        to: '/meus-dados',
        isAllowed: true,
      },
      {
        icon: <S.WorldIcon />,
        title: 'Comex',
        info: 'Acessar o comex.',
        to: 'https://comex.webcol.gerasinergia.com.br',
        href: 'https://comex.webcol.gerasinergia.com.br',
        isAllowed: !isPlannerUser,
      },
      /* Removido devido ao card: https://trello.com/c/p6V4NfMh
        {
        icon: <S.BarChartIcon />,
        title: 'Toller',
        info: 'Acessar o Toller.',
        to: 'https://toller.webcol.gerasinergia.com.br/',
        href: 'https://toller.webcol.gerasinergia.com.br/',
        isAllowed: !isPlannerUser,
      },
      */
      {
        icon: <WalletIcon />, 
        title: 'Carteira de Contratos',
        info: 'Acessar G Suite.',
        to: '#', 
        href: 'https://script.google.com/a/macros/4plsolutions.com.br/s/AKfycbyT7v5lHMKUOvviO-4RaMq75780ql_5LqcqjzyYv2iC-6gx_M7oM0_H76NB4xf936k/exec',
        isAllowed: !isPlannerUser && hasPermissionTo(['CARTEIRACONTRATOS.ACCESS']),
        requiresConfirmation: true, 
      },

      {
        icon: <S.ReverseLogisticsIcon />,
        title: 'Logística Reversa',
        info: 'Gerenciamento do processo de coleta de interditados.',
        to: '/logistica-reversa/coletas',
        // isAllowed: !isCarrierMember || profile?.userId === 91,
        isAllowed: !isPlannerUser,
      },
      {
        icon: <S.SettingsIcon />,
        title: 'Configurações',
        info: 'Acesso exclusivo para administradores do sistema.',
        to: '/configuracoes',
        isAllowed: hasPermissionTo(['ADMINS.CONFIGS']) && !isPlannerUser,
      },
      {
        icon: <S.ReservedIntervalIcon />,
        title: 'Bloqueio de Horários',
        info: 'Crie intervalos de bloqueio para docas',
        to: '/configuracoes/bloqueio-de-horarios',
        isAllowed: userBelongsToAnyOf('warehouseMember') && !isPlannerUser,
      },
    ];
  }, [
    isCarrierMember,
    profile?.userId,
    profile?.type,
    userBelongsToAnyOf,
    hasPermissionTo,
  ]);

  const isMenuActive = useCallback(
    (item: MenuItem): boolean => {
      return item.to.split('/')[1] === location.pathname.split('/')[1];
    },
    [location]
  );

  const handleExternalLinkClick = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    item: MenuItem
  ) => {
    if (!item.requiresConfirmation || !item.href) return;

    e.preventDefault();

    const confirmation = await confirmationDialogRef.current?.openDialog({
      title: 'Redirecionamento Externo',
      message: `Você será redirecionado para um ambiente fora do WebCOL. Deseja continuar?`,
    });

    if (confirmation === 'confirmed') {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <S.Container>
      <ConfirmationDialog ref={confirmationDialogRef} />
      <S.CollapseButton onClick={handleCollapse} recall={recall}>
        <S.ArrowIcon />
      </S.CollapseButton>
      <S.LeftPanel recall={recall}>
        <S.Header>
          <S.Logo>
            <S.LogoImage src={require('assets/images/light-logo.png')} />
          </S.Logo>
        </S.Header>
        <S.NavigationMenu>
          <TooltipProvider delayDuration={0}>
            {menuItems
              .filter(({ isAllowed }) => {
                return isAllowed;
              })
              .map((item, index) => {
                const entry = item.href ? (
                  <S.MenuHrefItem
                    href={item.href}
                    target="_blank"
                    aria-label={item.title}
                    onClick={(event) => handleExternalLinkClick(event, item)}
                  >
                    <S.MenuItemHeader>
                      {item.icon} <p>{item.title}</p>
                    </S.MenuItemHeader>
                    {item?.info && <S.MenuItemBody>{item.info}</S.MenuItemBody>}
                  </S.MenuHrefItem>
                ) : (
                  <S.MenuItem
                    to={item.to}
                    aria-label={item.title}
                    active={isMenuActive(item) ? true : undefined}
                  >
                    <S.MenuItemHeader>
                      {item.icon} <p>{item.title}</p>
                    </S.MenuItemHeader>
                    {item?.info && <S.MenuItemBody>{item.info}</S.MenuItemBody>}
                  </S.MenuItem>
                );
                return recall ? (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>{entry}</TooltipTrigger>
                    <TooltipContent side="right">{item.title}</TooltipContent>
                  </Tooltip>
                ) : (
                  <React.Fragment key={index}>{entry}</React.Fragment>
                );
              })}
          </TooltipProvider>
        </S.NavigationMenu>
        <S.Footer>
          {!recall && (
            <p>{new Date().getFullYear()} © Todos os direitos reservados</p>
          )}
        </S.Footer>
      </S.LeftPanel>
    </S.Container>
  );
};

export default LeftPanel;
